import React from 'react';
import { Box, Chip, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';

/**
 * SkillsConfirmationReadOnly component - displays confirmed skills in read-only mode for chat history
 * @param {Object} props - Component props
 * @param {Array} props.skills - Array of confirmed skill strings
 * @returns {JSX.Element} The rendered read-only component
 */
export const SkillsConfirmationReadOnly = ({ skills = [] }) => {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        borderRadius: '12px',
        border: '1px solid rgba(192, 197, 205, 0.5)',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        marginBottom: '12px'
      }}
    >
      {/* Content */}
      <Box
        sx={{
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        <Typography 
          sx={{ 
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '1.375em',
            color: '#101828'
          }}
        >
          Got it! Just to confirm – you're looking for candidates with the following skills, right? 👇
        </Typography>

        {/* Skills chips container */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            width: '100%',
          }}
        >
          {skills.map((skill, index) => (
            <Chip
              key={index}
              icon={
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: '14px !important',
                    color: '#0F42BC !important'
                  }} 
                />
              }
              label={skill}
              sx={{
                backgroundColor: '#E1EFFF',
                color: '#0F42BC',
                borderRadius: '16px',
                height: 'auto',
                padding: '4px 12px 4px 10px',
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: 'Roboto',
                lineHeight: '1.4285714285714286em',
                '& .MuiChip-label': {
                  padding: '0 4px 0 0',
                  color: '#0F42BC',
                },
                '& .MuiChip-icon': {
                  margin: '0 4px 0 0',
                  color: '#0F42BC !important',
                },
                border: 'none',
              }}
            />
          ))}
        </Box>

        {/* Confirmed Label */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 12px',
            borderRadius: '8px',
            bgcolor: '#F9FAFB',
            border: '1px solid #EAECF0',
            alignSelf: 'flex-start'
          }}
        >
          <Typography 
            sx={{ 
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              color: '#667085'
            }}
          >
            Confirmed
          </Typography>
        </Box>

        {/* Restart Discovery (Skills) */}
        <Button
          variant="text"
          size="small"
          onClick={async () => {
            try {
              await centralDiscoveryQueue.restartTopic('skills');
              if (typeof window !== 'undefined' && typeof window.goToIntakeView === 'function') {
                window.goToIntakeView();
              }
            } catch (e) {
              console.error('Failed to restart discovery for skills:', e);
            }
          }}
          sx={{
            textTransform: 'none',
            color: '#667085',
            alignSelf: 'flex-start',
            mt: 1,
            '&:hover': { backgroundColor: 'rgba(70, 83, 102, 0.04)' }
          }}
        >
          Restart discovery
        </Button>
      </Box>
    </Box>
  );
};

/**
 * SkillsConfirmation component - interactive skills confirmation with chip-based design
 * @param {Object} props - Component props
 * @param {Array} props.skills - Array of skill strings
 * @param {Function} props.onConfirmed - Callback when skills are confirmed
 * @param {Function} props.onAddMore - Callback when user wants to add more skills
 * @param {boolean} props.readOnly - Whether component is in read-only mode
 * @returns {JSX.Element} The rendered component
 */
const SkillsConfirmation = ({ skills, onConfirmed, onAddMore, readOnly = false }) => {
  const [selectedSkills, setSelectedSkills] = React.useState([]); // Start with no skills selected
  
  // If read-only, use the read-only component
  if (readOnly) {
    return <SkillsConfirmationReadOnly skills={skills} />;
  }
  
  // Handle skill toggle
  const toggleSkill = (skillToToggle) => {
    setSelectedSkills(prev => {
      if (prev.includes(skillToToggle)) {
        return prev.filter(skill => skill !== skillToToggle);
      } else {
        return [...prev, skillToToggle];
      }
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        borderRadius: '10px',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        marginBottom: '12px'
      }}
    >
      {/* Content */}
      <Box
        sx={{
          padding: '0 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        {/* Row wrapper with border */}
        <Box
          sx={{
            border: '1px solid rgba(192, 197, 205, 0.5)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          {/* Skills chips container */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              width: '100%',
            }}
          >
            {skills.map((skill, index) => {
              const isSelected = selectedSkills.includes(skill);
              return (
                <Chip
                  key={index}
                  icon={
                    isSelected ? (
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: '14px !important',
                          color: '#0F42BC !important'
                        }} 
                      />
                    ) : (
                      <RadioButtonUncheckedIcon 
                        sx={{ 
                          fontSize: '14px !important',
                          color: '#667085 !important'
                        }} 
                      />
                    )
                  }
                  label={skill}
                  onClick={() => toggleSkill(skill)}
                  sx={{
                    backgroundColor: isSelected ? '#E1EFFF' : '#F9FAFB',
                    color: isSelected ? '#0F42BC' : '#667085',
                    borderRadius: '16px',
                    height: 'auto',
                    padding: '4px 12px 4px 10px',
                    fontSize: '14px',
                    fontWeight: 400,
                    fontFamily: 'Roboto',
                    lineHeight: '1.4285714285714286em',
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      padding: '0 4px 0 0',
                      color: isSelected ? '#0F42BC' : '#667085',
                    },
                    '& .MuiChip-icon': {
                      margin: '0 4px 0 0',
                      color: isSelected ? '#0F42BC !important' : '#667085 !important',
                    },
                    border: '1px solid',
                    borderColor: isSelected ? '#0F42BC' : '#EAECF0',
                    '&:hover': {
                      backgroundColor: isSelected ? '#D1E7FF' : '#F3F4F6',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Buttons container */}
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
          }}
        >
          <Button
            variant="contained"
            onClick={() => onConfirmed(selectedSkills)}
            disabled={selectedSkills.length === 0}
            sx={{
              backgroundColor: '#4599FA',
              color: '#FFFFFF',
              borderRadius: '8px',
              padding: '8px 12px',
              height: '36px',
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: 'Roboto',
              lineHeight: '1.4285714285714286em',
              textTransform: 'none',
              alignSelf: 'flex-start',
              '&:hover': {
                backgroundColor: '#3A7BD5',
              },
            }}
          >
            Confirmed
          </Button>

          <Button
            variant="outlined"
            onClick={onAddMore}
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#969DAD',
              borderColor: '#EAECF0',
              borderRadius: '8px',
              padding: '8px 12px',
              height: '36px',
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: 'Roboto',
              lineHeight: '1.4285714285714286em',
              textTransform: 'none',
              alignSelf: 'flex-start',
              '&:hover': {
                backgroundColor: '#F9FAFB',
                borderColor: '#EAECF0',
                color: '#969DAD',
              },
            }}
          >
            I have more to add
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SkillsConfirmation;
